import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import './editor.css';
import { asset } from '../config';
import { parseRouteFile } from '../design/gpx';
import { emptyDesign, type Camera, type Route } from '../design/schema';
import type { PosterHandle } from '../poster/Poster';
import { track } from '../services/analytics';
import { loadDesign, loadServerConfig, type ServerConfig } from '../services/api';
import { loadVariants, priceFor } from '../services/shopify';
import { connectStrava } from '../services/strava';
import CartBar from './CartBar';
import { IconFrame, IconLabel, IconMap, IconRoute, IconStyle, IconText } from './icons';
import { LabelsPanel, MapPanel, RoutePanel, SizePanel, StartPanel, StylePanel, TextPanel, useAddRoutes, withinPointBudget } from './panels';
import PreviewModal from './PreviewModal';
import Stage from './Stage';
import { EditorContext, clearDraft, initialState, loadDraft, reducer, saveDraft, type Editor, type TabId } from './store';
import StravaPicker from './StravaPicker';
import { useCloudSync } from './useCloudSync';

const TABS: Array<{ id: TabId; label: string; icon: JSX.Element }> = [
  { id: 'route', label: 'Route', icon: <IconRoute /> },
  { id: 'style', label: 'Style', icon: <IconStyle /> },
  { id: 'size', label: 'Size', icon: <IconFrame /> },
  { id: 'text', label: 'Text', icon: <IconText /> },
  { id: 'map', label: 'Position', icon: <IconMap /> },
  { id: 'labels', label: 'Labels', icon: <IconLabel /> },
];

const requestedDesignId = () => new URLSearchParams(location.search).get('design');
/**
 * ?design=ID&tryit=true opens a design to play with: the route stays as it is (no upload,
 * Strava or Route tab) and there is no saving or ordering. Nothing the visitor does touches
 * the design behind the link.
 */
const isTryIt = () => ['true', '1'].includes(new URLSearchParams(location.search).get('tryit') ?? '');

export default function App() {
  const [state, dispatch] = useReducer(reducer, undefined, () => initialState());
  const edit = useCallback<Editor['edit']>((recipe, key) => dispatch({ type: 'edit', recipe, key, at: Date.now() }), []);
  const editor = useMemo<Editor>(() => ({ state, dispatch, edit }), [state, edit]);
  const { design } = state;
  const hasRoute = design.routes.length > 0;

  const rootRef = useRef<HTMLDivElement>(null);
  const posterRef = useRef<PosterHandle>(null);
  const [boot, setBoot] = useState<'loading' | 'ready'>(requestedDesignId() ? 'loading' : 'ready');
  const [tryIt] = useState(() => isTryIt() && Boolean(requestedDesignId()));
  const [notice, setNotice] = useState<string | null>(null);
  const [draft, setDraft] = useState(() => (requestedDesignId() ? null : loadDraft()));
  const [server, setServer] = useState<ServerConfig>({ strava: null });
  const [variants, setVariants] = useState<Awaited<ReturnType<typeof loadVariants>>>([]);
  const [preview, setPreview] = useState(false);
  const [strava, setStrava] = useState<Promise<string> | null>(null);
  const sync = useCloudSync(design, state.locked, dispatch);
  // Lives here so upload errors survive the switch from the start screen to the editor.
  const adder = useAddRoutes(editor);

  // Open a saved design from its link.
  useEffect(() => {
    const id = requestedDesignId();
    if (!id) return;
    loadDesign(id)
      .then((saved) => {
        if (tryIt) {
          // A copy with no id: it can be changed freely and is never written back.
          dispatch({ type: 'load', design: { ...saved.design, id: null }, tab: 'style' });
          return;
        }
        dispatch({ type: 'load', design: saved.design, locked: saved.status === 'ordered' });
        sync.markSaved(saved.design);
        if (saved.status === 'ordered') setNotice('This design has been ordered, so it is locked. Any changes you make here are saved as a new copy.');
      })
      .catch((e) => setNotice(e.status === 404 ? "We couldn't find that saved design. It may have been removed." : `Couldn't load your saved design: ${e.message}`))
      .finally(() => setBoot('ready'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadServerConfig().then(setServer);
    loadVariants().then(setVariants);
  }, []);

  // Keep a copy in this browser so a closed tab or a dropped connection never loses work.
  useEffect(() => {
    if (boot !== 'ready' || !hasRoute || tryIt) return;
    const timer = setTimeout(() => saveDraft(design), 400);
    return () => clearTimeout(timer);
  }, [design, boot, hasRoute, tryIt]);

  // The editor fills the first screen below the store header.
  useEffect(() => {
    const el = rootRef.current!;
    const measure = () => el.style.setProperty('--tmd-top', `${Math.max(0, Math.round(el.getBoundingClientRect().top + window.scrollY))}px`);
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  // Keyboard: undo/redo, delete the selected label, escape placing mode.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const typing = e.target instanceof HTMLElement && (e.target.isContentEditable || /^(INPUT|TEXTAREA|SELECT)$/.test(e.target.tagName));
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z' && !typing) {
        e.preventDefault();
        dispatch({ type: e.shiftKey ? 'redo' : 'undo' });
      } else if (e.key === 'Escape' && state.placing) {
        dispatch({ type: 'placing', on: false });
      } else if ((e.key === 'Delete' || e.key === 'Backspace') && !typing && state.selectedLabel) {
        const id = state.selectedLabel;
        edit((d) => ({ ...d, labels: d.labels.filter((l) => l.id !== id) }));
        dispatch({ type: 'selectLabel', id: null });
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state.placing, state.selectedLabel, edit]);

  // --- camera controls shared by the toolbar and the Position tab -----------
  const liveCamera = (): Camera => posterRef.current!.getCamera();
  const onZoom = (delta: number) => {
    const c = liveCamera();
    edit((d) => ({ ...d, camera: { ...c, zoom: Math.max(1, Math.min(18, c.zoom + delta)) } }), 'camera');
  };
  const onRotate = (bearing: number) => {
    const c = liveCamera();
    if (c.bearing !== bearing) edit((d) => ({ ...d, camera: { ...c, bearing } }), 'camera-rotate');
  };
  const onFit = () => edit((d) => ({ ...d, camera: null }));

  const addRoute = (route: Route) => {
    edit((d) => ({ ...d, routes: withinPointBudget([...d.routes, route]), camera: null }));
    track('designer_route_added', { source: route.source, routes: 1 });
  };

  const loadSample = async () => {
    try {
      const file = 'samples/Courgenay_Ballon-DAlsace.gpx';
      const text = await fetch(asset(file)).then((r) => r.text());
      const [route] = parseRouteFile(text, 'Ballon d’Alsace.gpx');
      dispatch({
        type: 'load',
        design: {
          ...emptyDesign(),
          routes: [{ ...route, name: 'Courgenay to Ballon d’Alsace', source: 'sample' }],
          text: { ...emptyDesign().text, title: 'Ballon d’Alsace', subtitle: 'Vosges, France' },
        },
      });
    } catch {
      setNotice("Couldn't load the sample route.");
    }
  };

  const restart = () => {
    if (!window.confirm('Start a new design? Your current one will be cleared from this device.')) return;
    clearDraft();
    setDraft(null);
    const url = new URL(location.href);
    url.searchParams.delete('design');
    history.replaceState(null, '', url);
    dispatch({ type: 'load', design: emptyDesign() });
  };

  const price = priceFor(variants, design.product);
  // The Strava window has to be opened directly by the click (Safari blocks it otherwise),
  // so sign-in starts here and the picker follows it.
  const stravaId = server.strava?.clientId;
  const openStrava = stravaId ? () => setStrava(connectStrava(stravaId)) : null;

  return (
    <EditorContext.Provider value={editor}>
      <div className={`tmd${hasRoute ? '' : ' is-start'}${tryIt ? ' is-tryit' : ''}`} ref={rootRef}>
        <Stage
          ref={posterRef}
          onZoom={onZoom}
          onFit={onFit}
          onPreview={() => {
            setPreview(true);
            track('designer_preview');
          }}
        />

        <div className="tmd-side">
          {notice && (
            <div className="tmd-notice" role="status">
              <span>{notice}</span>
              <button type="button" className="tmd-linkbtn" onClick={() => setNotice(null)}>
                Dismiss
              </button>
            </div>
          )}
          {boot === 'loading' ? (
            <div className="tmd-panel">
              <p className="tmd-muted">
                <span className="tmd-spinner" /> Loading your design…
              </p>
            </div>
          ) : tryIt && !hasRoute ? (
            <div className="tmd-panel">
              <p className="tmd-muted">{notice ? '' : "This design isn't available to try."}</p>
            </div>
          ) : !hasRoute ? (
            <StartPanel
              draft={draft}
              adder={adder}
              onResume={() => draft && dispatch({ type: 'load', design: draft.design })}
              onStrava={openStrava}
              onSample={loadSample}
            />
          ) : (
            <>
              <nav className="tmd-tabs" aria-label="Design steps">
                {TABS.filter((t) => !(tryIt && t.id === 'route')).map((t) => (
                  <button key={t.id} type="button" className={t.id === state.tab ? 'is-on' : ''} aria-current={t.id === state.tab} onClick={() => dispatch({ type: 'tab', tab: t.id })}>
                    {t.icon}
                    <span>{t.label}</span>
                  </button>
                ))}
              </nav>
              <div className="tmd-scroll">
                {state.tab === 'route' && <RoutePanel adder={adder} onStrava={openStrava} onRestart={restart} />}
                {state.tab === 'style' && <StylePanel />}
                {state.tab === 'text' && <TextPanel />}
                {state.tab === 'labels' && <LabelsPanel />}
                {state.tab === 'map' && <MapPanel onZoom={onZoom} onFit={onFit} onRotate={onRotate} />}
                {state.tab === 'size' && <SizePanel price={price} />}
                {!tryIt && (
                  <p className="tmd-sync" data-status={sync.status}>
                    {sync.status === 'saving' && 'Saving…'}
                    {sync.status === 'saved' && 'All changes saved to your link'}
                    {sync.status === 'error' && "Couldn't reach the server. Your work is still saved on this device."}
                    {sync.status === 'local' && 'Saved on this device'}
                  </p>
                )}
              </div>
              {!tryIt && <CartBar price={price} sync={sync} />}
            </>
          )}
        </div>

        {preview && <PreviewModal design={design} onClose={() => setPreview(false)} />}
        {strava && stravaId && <StravaPicker clientId={stravaId} initial={strava} onAdd={addRoute} onClose={() => setStrava(null)} />}
      </div>
    </EditorContext.Provider>
  );
}
